import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./AdminUserManage.css";

const api = (userId, suffix = "archives") => `/api/admin/users/${encodeURIComponent(userId)}/${suffix}`;
const ACCOUNT_LANGUAGES = [
  ["en", "English"], ["es", "Español"], ["fr", "Français"], ["pt", "Português"],
  ["et", "Eesti"], ["no", "Norsk"], ["de", "Deutsch"], ["zh", "中文"], ["ja", "日本語"]
];
const accountLanguageLabel = (code) => ACCOUNT_LANGUAGES.find(([value]) => value === code)?.[1] || "Not set";

function date(value) {
  return value ? new Date(value).toLocaleString() : "—";
}

function Pager({ data, onPage }) {
  if (!data) return null;
  return (
    <div className="customer-archive-pager">
      <button type="button" disabled={data.page <= 0} onClick={() => onPage(data.page - 1)}>Previous</button>
      <span>Page {data.page + 1}</span>
      <button type="button" disabled={!data.hasMore} onClick={() => onPage(data.page + 1)}>Next</button>
    </div>
  );
}

function ArchiveTable({ title, count, children, pager, onPage, empty = "No records in this period." }) {
  return (
    <section className="customer-archive-section">
      <div className="customer-archive-section-head">
        <div><h3>{title}</h3><span>{count ?? 0} shown on this page</span></div>
        {pager && <Pager data={pager} onPage={onPage} />}
      </div>
      {count ? <div className="customer-archive-table-wrap"><table><tbody>{children}</tbody></table></div> : <p className="customer-archive-empty">{empty}</p>}
    </section>
  );
}

function AdminUserManage() {
  const userId = window.location.pathname.split("/").pop();
  const [archiveMonths, setArchiveMonths] = useState(6);
  const [reason, setReason] = useState("");
  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editReason, setEditReason] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [support, setSupport] = useState({ category: "support", reference: "", title: "", note: "", reason: "" });
  const [savingNote, setSavingNote] = useState(false);
  const [statusChanges, setStatusChanges] = useState({});

  const loadArchive = async (pages = {}) => {
    if (reason.trim().length < 10) {
      setError("Add a short reason for accessing this customer record (at least 10 characters).");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetch(api(userId), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim(), archiveMonths, ...pages })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || "Could not load the customer archive.");
      if (window.location.pathname.split("/").pop() !== userId) return;
      setArchive(data);
    } catch (requestError) {
      setError(requestError.message === "Failed to fetch"
        ? "Admin service unavailable. Confirm the local backend and LAB database are running."
        : requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    if (editReason.trim().length < 10) {
      setError("Add a reason for this profile change (at least 10 characters).");
      return;
    }
    setSavingProfile(true);
    setError("");
    try {
      const response = await fetch(api(userId, "profile"), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: archive.user.name || "", username: archive.user.username || "", birthDate: archive.user.birth_date || "", preferredLanguage: archive.user.preferred_language || "", reason: editReason.trim() })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || "Could not update the profile.");
      setArchive((current) => ({ ...current, user: { ...current.user, ...data.user } }));
      setEditReason("");
      await loadArchive();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSupportNote = async (event) => {
    event.preventDefault();
    setSavingNote(true);
    setError("");
    try {
      const response = await fetch(api(userId, "support-notes"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(support)
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || "Could not save the support note.");
      setSupport({ category: "support", reference: "", title: "", note: "", reason: "" });
      await loadArchive();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingNote(false);
    }
  };

  const updateSupportStatus = async (event, noteId) => {
    event.preventDefault();
    const change = statusChanges[noteId] || {};
    if ((change.reason || "").trim().length < 10) {
      setError("Add a reason for changing the support record status.");
      return;
    }
    try {
      const response = await fetch(api(userId, `support-notes/${noteId}`), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: change.status, reason: change.reason.trim() })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || "Could not update support record.");
      setStatusChanges((current) => ({ ...current, [noteId]: undefined }));
      await loadArchive();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  useEffect(() => {
    setArchive(null);
    setReason("");
    setError("");
    setEditReason("");
    setSupport({ category: "support", reference: "", title: "", note: "", reason: "" });
    setStatusChanges({});
  }, [userId]);

  return (
    <section className="bubly-admin-user-manage">
      <div className="customer-record-toolbar">
        <Link to="/admin/search">← Back to customer search</Link>
        <span>PRIVATE CUSTOMER RECORD · ACCESS IS AUDITED</span>
      </div>

      <div className="bubly-admin-panel customer-record-access">
        <div className="bubly-admin-panel-header">
          <span>CUSTOMER SUPPORT</span>
          <h2>Customer record · #{userId}</h2>
        </div>
        <form onSubmit={(event) => { event.preventDefault(); loadArchive(); }}>
          <label className="customer-record-label" htmlFor="archive-reason">Why do you need this customer’s record?</label>
          <textarea id="archive-reason" value={reason} onChange={(event) => setReason(event.target.value)} minLength={10} maxLength={500} placeholder="For example: support case #1234, customer requested help accessing their account." required />
          <div className="customer-record-access-row">
            <label>History window
              <select value={archiveMonths} onChange={(event) => setArchiveMonths(Number(event.target.value))}>
                <option value={6}>Last 6 months</option>
                <option value={12}>Last 12 months</option>
              </select>
            </label>
            <button type="submit" disabled={loading}>{loading ? "Loading record…" : archive ? "Refresh customer record" : "Open customer record"}</button>
          </div>
          <p className="customer-record-hint">Each request to view or refresh archive data is recorded with your admin account, the reason, time, and connection details.</p>
        </form>
      </div>

      {error && <div className="customer-record-error" role="alert">{error}</div>}

      {archive && (
        <>
          <div className="bubly-admin-panel customer-record-profile">
            <div className="customer-record-profile-heading">
              {archive.user.avatar && /^https?:\/\//i.test(archive.user.avatar)
                ? <img className="admin-user-avatar" src={archive.user.avatar} alt="Customer profile" referrerPolicy="no-referrer" />
                : <div className="admin-user-avatar">{(archive.user.name || archive.user.username || "U").charAt(0).toUpperCase()}</div>}
              <div><span>CLIENT PROFILE</span><h2>{archive.user.name || archive.user.username || "Unnamed account"}</h2><p>{archive.user.email} · User #{archive.user.id}</p></div>
              <span className="bubly-admin-status">{archive.user.account_status || archive.user.status || "Unknown status"}</span>
            </div>
            <div className="customer-record-facts">
              <div><span>Phone</span><strong>{archive.user.phone || "Not provided"}</strong></div>
              <div><span>Date of birth</span><strong>{archive.user.birth_date ? new Date(`${archive.user.birth_date.slice(0, 10)}T00:00:00`).toLocaleDateString() : "Not provided"}</strong></div>
              <div><span>Account created</span><strong>{date(archive.user.created_at)}</strong></div>
              <div><span>Latest profile location</span><strong>{[archive.user.geo_city, archive.user.geo_region, archive.user.geo_country || archive.user.country].filter(Boolean).join(", ") || "Unknown"}</strong></div>
              <div><span>Latest network address</span><strong>{archive.user.ip_address || "Not available"}</strong></div>
              <div><span>Latest device</span><strong>{[archive.user.device_type, archive.user.os_name, archive.user.browser_name].filter(Boolean).join(" · ") || "Not available"}</strong></div>
              <div><span>Account language</span><strong>{accountLanguageLabel(archive.user.preferred_language)}</strong></div>
              <div><span>Detected device language / time zone</span><strong>{[archive.user.language, archive.user.timezone].filter(Boolean).join(" · ") || "Not available"}</strong></div>
            </div>
          </div>

          <section className="bubly-admin-panel customer-profile-edit">
            <div className="customer-archive-section-head"><div><h3>Edit customer profile</h3><span>Profile details and the account language are editable here. Every change is audit logged.</span></div></div>
            <form onSubmit={saveProfile}>
              <label>Display name<input value={archive.user.name || ""} onChange={(event) => setArchive((current) => ({ ...current, user: { ...current.user, name: event.target.value } }))} maxLength={100} /></label>
              <label>Username<input value={archive.user.username || ""} onChange={(event) => setArchive((current) => ({ ...current, user: { ...current.user, username: event.target.value } }))} minLength={3} maxLength={24} pattern="[a-zA-Z0-9_.-]{3,24}" /></label>
              <label>Date of birth<input type="date" value={archive.user.birth_date ? archive.user.birth_date.slice(0, 10) : ""} onChange={(event) => setArchive((current) => ({ ...current, user: { ...current.user, birth_date: event.target.value } }))} /></label>
              <label>Account language<select value={archive.user.preferred_language || ""} onChange={(event) => setArchive((current) => ({ ...current, user: { ...current.user, preferred_language: event.target.value } }))}><option value="">Not set</option>{ACCOUNT_LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
              <label className="customer-record-full">Reason for the change<textarea value={editReason} onChange={(event) => setEditReason(event.target.value)} minLength={10} maxLength={500} required /></label>
              <p className="customer-record-full customer-record-hint">Verified phone numbers and account access status are not changed here. Route verification or a separate approved account action is required for those changes.</p>
              <button type="submit" disabled={savingProfile}>{savingProfile ? "Saving…" : "Save profile change"}</button>
            </form>
          </section>

          <section className="bubly-admin-panel customer-support-panel">
            <div className="customer-archive-section-head"><div><h3>Support and legal request log</h3><span>Internal notes are retained with the customer record.</span></div></div>
            <form onSubmit={saveSupportNote}>
              <label>Record type<select value={support.category} onChange={(event) => setSupport({ ...support, category: event.target.value })}><option value="support">Support case</option><option value="legal_request">Legal request</option><option value="account_review">Account review</option></select></label>
              <label>Case / request reference<input value={support.reference} onChange={(event) => setSupport({ ...support, reference: event.target.value })} maxLength={100} /></label>
              <label className="customer-record-full">Title<input value={support.title} onChange={(event) => setSupport({ ...support, title: event.target.value })} minLength={3} maxLength={160} required /></label>
              <label className="customer-record-full">What was requested or done?<textarea value={support.note} onChange={(event) => setSupport({ ...support, note: event.target.value })} minLength={10} maxLength={5000} required /></label>
              <label className="customer-record-full">Reason / authority for recording this note<textarea value={support.reason} onChange={(event) => setSupport({ ...support, reason: event.target.value })} minLength={10} maxLength={500} required /></label>
              <button type="submit" disabled={savingNote}>{savingNote ? "Saving…" : "Add support record"}</button>
            </form>
            <div className="customer-note-list">{archive.archive.supportNotes.map((note) => <article key={note.id}><div><strong>{note.title}</strong><span>{note.category.replaceAll("_", " ")} · {note.status} · {date(note.created_at)}</span></div>{note.reference && <small>Reference: {note.reference}</small>}<p>{note.note}</p><small>Recorded because: {note.reason}</small><small>Added by {note.agent_name || note.agent_email}</small><form className="customer-note-status-form" onSubmit={(event) => updateSupportStatus(event, note.id)}><select value={statusChanges[note.id]?.status || note.status} onChange={(event) => setStatusChanges((current) => ({ ...current, [note.id]: { ...current[note.id], status: event.target.value } }))}><option value="open">Open</option><option value="pending">Pending</option><option value="resolved">Resolved</option></select><input value={statusChanges[note.id]?.reason || ""} onChange={(event) => setStatusChanges((current) => ({ ...current, [note.id]: { ...current[note.id], reason: event.target.value } }))} minLength={10} maxLength={500} placeholder="Reason for status change" required /><button type="submit">Update status</button></form></article>)}</div>
          </section>

          <div className="customer-archive-grid">
            <ArchiveTable title="Network, IP and location history" count={archive.archive.network.items.length} pager={archive.archive.network} onPage={(page) => loadArchive({ networkPage: page })}>
              {archive.archive.network.items.map((item) => <tr key={item.id}><td><strong>{date(item.observed_at)}</strong><small>{item.source || "—"} · {item.event_type || "—"}</small></td><td><strong>{item.ip_address || "—"}</strong><small>Approximate IP location: {[item.city, item.region, item.country].filter(Boolean).join(", ") || "Unavailable"}{item.latitude != null && item.longitude != null ? ` · ${item.latitude}, ${item.longitude}` : ""}</small></td><td><strong>{[item.device_type, item.os_name, item.browser_name].filter(Boolean).join(" · ") || "Device unknown"}</strong><small>{item.timezone || "—"} · {item.language || "—"}</small></td><td><strong>{[item.proxy_status, item.vpn_status, item.tor_status, item.datacenter_status].filter(Boolean).join(" · ") || "Network checks unavailable"}</strong><small>{item.organization || item.asn || ""}</small></td></tr>)}
            </ArchiveTable>

            <ArchiveTable title="Legal location decisions" count={archive.archive.legalLocation.items.length + (archive.archive.currentLegalLocation ? 1 : 0)} pager={archive.archive.legalLocation} onPage={(page) => loadArchive({ legalPage: page })}>
              {archive.archive.currentLegalLocation && <tr><td><strong>Current decision · {archive.archive.currentLegalLocation.legal_profile}</strong><small>{archive.archive.currentLegalLocation.country_code} · {archive.archive.currentLegalLocation.region_code || "—"} · {date(archive.archive.currentLegalLocation.checked_at)}</small></td><td>Confidence {archive.archive.currentLegalLocation.confidence}</td></tr>}
              {archive.archive.legalLocation.items.map((item) => <tr key={item.id}><td><strong>{item.legal_profile}</strong><small>{item.country_code} · {item.region_code || "—"} · {date(item.checked_at)}</small></td><td><strong>Confidence {item.confidence}</strong><small>{JSON.stringify(item.evidence || {})}</small></td></tr>)}
            </ArchiveTable>

            <ArchiveTable title="Connected sessions" count={archive.archive.sessions.items.length} pager={archive.archive.sessions} onPage={(page) => loadArchive({ sessionPage: page })} empty="No current or retained sessions in this period.">
              {archive.archive.sessions.items.map((item) => <tr key={item.id}><td><strong>{date(item.last_used_at || item.expires_at)}</strong><small>Session #{item.id} · last used / expiry</small></td><td><strong>{item.ip_address || "—"}</strong><small>{item.user_agent || "Device details unavailable"}</small></td></tr>)}
            </ArchiveTable>

            <ArchiveTable title="Device recognition events" count={archive.archive.deviceEvents.items.length} pager={archive.archive.deviceEvents} onPage={(page) => loadArchive({ fingerprintPage: page })} empty="No events are available. Optional device recognition may have been declined.">
              {archive.archive.deviceEvents.items.map((item, index) => <tr key={`${item.recorded_at}-${index}`}><td><strong>{date(item.recorded_at)}</strong><small>{item.ip_address || "—"}</small></td><td><strong>{[item.device_type, item.os_name, item.browser_name].filter(Boolean).join(" · ") || "Device unavailable"}</strong><small>{item.user_agent || "—"}</small></td><td>{item.timezone || "—"} · {item.language || "—"}</td></tr>)}
            </ArchiveTable>
          </div>

          <section className="bubly-admin-panel customer-access-log">
            <div className="customer-archive-section-head"><div><h3>Archive access history</h3><span>Recent staff access to this customer’s record</span></div></div>
            <div className="customer-note-list">{archive.archive.accessLog.map((entry) => <article key={entry.id}><strong>{entry.admin_name || entry.admin_email} · {entry.action.replaceAll("_", " ")}</strong><span>{date(entry.created_at)} · {entry.ip_address || "IP unavailable"}</span><p>{entry.reason}</p>{entry.details && Object.keys(entry.details).length > 0 && <small>Details: {JSON.stringify(entry.details)}</small>}</article>)}</div>
          </section>
        </>
      )}
    </section>
  );
}

export default AdminUserManage;
